import { ShareTicketBody } from "@mg/schemas/src/christo/catalyst";
import {
  useNavigate,
  useRouter,
  useSearch,
  useLoaderData,
} from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { useMemo, useRef, useState } from "react";

import { WorkflowGrid } from "./components/WorkflowGrid";
import { EmptyWorkflowsView } from "./EmptyWorkflowsView";
import { workflowsRoute } from "./route";

import { DeleteWorkflowDialog } from "../../components/dialogs/DeleteWorkflowDialog";
import { useUI } from "../../contexts/ui";
import { useAppSelector } from "../../utils/hooks";
import { downloadAsset } from "../../utils/http";
import {
  useDownloadFolderLink,
  useShareFolder,
  useShareFolderLink,
} from "../../utils/queries/folders";
import {
  useDeleteFolderMutation,
  useShareTicket,
  useShareTicketLink,
  useUpdateFolderMutation,
} from "../../utils/queries/projects";
import { queryClient } from "../../utils/queryClient";
import {
  ShareDialog,
  type ShareDialogProps,
} from "../tickets/components/dialogs/ShareDialog";
import { NoTicketsFoundView } from "../tickets/components/NoTicketsFoundView";
import { TicketsHeader } from "../tickets/components/TicketsHeader";
import { RenameDialog } from "../tickets/components/UpdateNameDialog";

export function Workflows() {
  const { recentWorkflows } = useLoaderData({ from: workflowsRoute.id });
  const folders = useAppSelector((state) => state.punttProjects.folders);

  const router = useRouter();
  const navigate = useNavigate({ from: workflowsRoute.to });
  const { folderId, participants, ticketTitle } = useSearch({
    from: workflowsRoute.id,
  });
  const posthog = usePostHog();
  const { notify } = useUI();

  const deleteFolderMutation = useDeleteFolderMutation();
  const folderShareLinkMutation = useShareFolderLink();
  const shareFolderMutation = useShareFolder();
  const ticketShareLinkMutation = useShareTicketLink();
  const shareTicketMutation = useShareTicket();
  const folderDownloadMutation = useDownloadFolderLink();
  const updateFolderMutation = useUpdateFolderMutation();

  const [renamingId, setRenamingId] = useState<string>();
  const [deletingFolderId, setDeletingFolderId] = useState<string>();
  const [shareItem, setShareItem] = useState<ShareDialogProps["item"]>();
  const [shareLink, setShareLink] = useState<string>();
  const foldersUploadRef = useRef<HTMLInputElement>(null);

  const [didSearch, setDidSearch] = useState(() =>
    Boolean(ticketTitle?.length || participants?.length),
  );

  const workflowName = useMemo(() => {
    if (renamingId == null) {
      return undefined;
    }

    const workflow = recentWorkflows?.find((f) => f._id === renamingId);
    return workflow?.name;
  }, [renamingId, recentWorkflows]);
  function handleOpenFolder(folderId: string) {
    navigate({
      to: workflowsRoute.to,
      search(prev) {
        return {
          ...prev,
          folderId,
          view: "workflow",
        };
      },
    });
  }

  function handleShareFolder(folderId: string, emails: string[]) {
    const startTime = performance.now();
    shareFolderMutation.mutate(
      { folderId, emails },
      {
        onSuccess() {
          const folder = (recentWorkflows ?? []).find(
            (f) => f._id === folderId,
          );

          posthog.capture("emailed_folder_share_link", {
            folder_id: folderId,
            folder_name: folder?.name,
            emails,
            num_shares: emails.length,
            duration: performance.now() - startTime,
          });

          setShareLink(undefined);
          setShareItem(undefined);
          notify({
            title: "Folder shared",
            message: `Email has been sent to ${emails.length} recipient(s)`,
          });
        },
      },
    );
  }

  function handleDownloadFolder(folderId: string) {
    const startTime = performance.now();
    folderDownloadMutation.mutate(folderId, {
      onSuccess(data) {
        const folder = folders.find((f) => f._id === folderId);

        posthog.capture("download_folder", {
          folder_id: folderId,
          folder_name: folder?.name,
          data_length: data?.url.length,
          request_duration: performance.now() - startTime,
        });
        if (data) {
          downloadAsset(data.url);
        }
      },
    });
  }

  function renderTickets() {
    if (recentWorkflows?.length === 0 && didSearch) {
      return <NoTicketsFoundView entityName="workflows" />;
    } else if (recentWorkflows?.length === 0) {
      return <EmptyWorkflowsView />;
    }

    return (
      <WorkflowGrid
        handleOpenFolder={handleOpenFolder}
        onDeleteFolder={setDeletingFolderId}
        onRenameFolder={setRenamingId}
        onShareFolder={(folderId: string) => {
          setShareLink(undefined);
          setShareItem({ id: folderId, type: "workflow" });
          folderShareLinkMutation.mutate(folderId, {
            onSuccess(data) {
              if (data == null) {
                throw new Error("An unknown error occurred");
              }

              setShareLink(data.url);
            },
          });
        }}
        onDownloadFolder={handleDownloadFolder}
      />
    );
  }

  function handleSearchInput(hasUserSearched: boolean) {
    setDidSearch(hasUserSearched);
  }

  return (
    <>
      <ShareDialog
        item={shareItem}
        latestVersionNumber={undefined}
        shareLink={shareLink}
        shareLinkPending={
          folderShareLinkMutation.isPending || ticketShareLinkMutation.isPending
        }
        isPending={
          shareFolderMutation.isPending || shareTicketMutation.isPending
        }
        onCancel={() => setShareItem(undefined)}
        onShare={(
          emails: string[],
          _shareVersion: ShareTicketBody["version"],
        ) => {
          if (shareItem == null) {
            throw new Error("No item to share");
          }

          switch (shareItem.type) {
            case "folder":
              return handleShareFolder(shareItem.id, emails);
            default:
              throw new Error("Unknown share item type");
          }
        }}
        onVersionChange={(version) =>
          shareTicketMutation.mutate(
            { ticketId: shareItem!.id, version },
            {
              onSuccess(data) {
                if (data == null) {
                  throw new Error("An unknown error occurred");
                }

                setShareLink(data.url);
              },
            },
          )
        }
      />
      <RenameDialog
        isPending={updateFolderMutation.isPending}
        _id={renamingId}
        onCancel={() => setRenamingId(undefined)}
        onSave={(_id, folderName) => {
          return updateFolderMutation.mutateAsync(
            { _id, name: folderName },
            {
              onSuccess() {
                setRenamingId(undefined);
                posthog.capture("folder_renamed", {
                  folder_id: _id,
                  folder_name: folderName,
                });
                return router.invalidate();
              },
            },
          );
        }}
        name={workflowName}
      />
      <DeleteWorkflowDialog
        isPending={deleteFolderMutation.isPending}
        folderId={deletingFolderId}
        onCancel={() => setDeletingFolderId(undefined)}
        onDelete={() => {
          if (deletingFolderId == null) {
            throw new Error(
              "The folder we want to delete cannot have a null or undefined ID",
            );
          }

          deleteFolderMutation.mutate(deletingFolderId, {
            onSuccess() {
              if (folderId === deletingFolderId) {
                navigate({
                  search: ({ folderId: _folderId, ...rest }) => rest,
                });
              }
              setDeletingFolderId(undefined);
              posthog.capture("folder_deleted", {
                folder_id: deletingFolderId,
              });
              queryClient.invalidateQueries({ queryKey: ["folders", "tree"] });
              return router.invalidate();
            },
            onError() {
              window.alert("Unable to delete folder. Please try again.");
            },
          });
        }}
      />
      <article className="grid grid-rows-[auto_1fr] content-start overflow-auto pt-6">
        <TicketsHeader
          isPending={false}
          onSearch={handleSearchInput}
          onFilesUpload={() => {}}
          onFolderUpload={() => foldersUploadRef.current?.click()}
          onNewFolder={() => {
            console.debug("Intentionally unimplemented");
          }}
          onDeleteFolder={setDeletingFolderId}
          onRenameFolder={() => {
            console.debug("Intentionally unimplemented");
          }}
          onShareFolder={(folderId: string) => {
            setShareLink(undefined);
            setShareItem({ id: folderId, type: "workflow" });
            folderShareLinkMutation.mutate(folderId, {
              onSuccess(data) {
                if (data == null) {
                  throw new Error("An unknown error occurred");
                }

                setShareLink(data.url);
              },
            });
          }}
          onDownloadFolder={handleDownloadFolder}
        />
        {renderTickets()}
      </article>
    </>
  );
}
